// apps/web/src/pages/admin/EmailTestPage.tsx

import React, { useState } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Send, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const EmailTestPage: React.FC = () => {
    const [recipientEmail, setRecipientEmail] = useState('');
    const [templateType, setTemplateType] = useState<'welcome' | 'verification' | 'approval' | 'notification' | 'custom'>('welcome');
    const [customSubject, setCustomSubject] = useState('');
    const [customMessage, setCustomMessage] = useState('');

    const { data: emailStatus, isLoading: statusLoading } = trpc.emailTest.checkEmailStatus.useQuery();

    const sendTestEmail = trpc.emailTest.sendTestEmail.useMutation({
        onSuccess: (data: any) => {
            toast.success(`✅ ${data.message}`);
            setRecipientEmail('');
            setCustomSubject('');
            setCustomMessage('');
        },
        onError: (error: any) => {
            toast.error(`❌ ${error.message}`);
        },
    });

    const handleSendTest = () => {
        if (!recipientEmail) {
            toast.error('Please enter a recipient email');
            return;
        }

        sendTestEmail.mutate({
            recipientEmail,
            templateType,
            customSubject: templateType === 'custom' ? customSubject : undefined,
            customMessage: templateType === 'custom' ? customMessage : undefined,
        });
    };

    return (
        <div className="container mx-auto py-6 px-4 max-w-4xl animate-fade-in">
            <Card className="glass">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-2xl">
                        <Mail className="h-6 w-6 text-primary" />
                        Email System Testing
                    </CardTitle>
                    <CardDescription>
                        Test your Resend email configuration and send test emails to verify delivery.
                    </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                    {/* Email Status */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Email Service Status</h3>
                        {statusLoading ? (
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Checking status...</span>
                            </div>
                        ) : emailStatus ? (
                            <div className="space-y-2">
                                <Alert variant={emailStatus.emailServiceConfigured ? 'default' : 'destructive'}>
                                    {emailStatus.emailServiceConfigured ? (
                                        <CheckCircle className="h-4 w-4" />
                                    ) : (
                                        <XCircle className="h-4 w-4" />
                                    )}
                                    <AlertTitle>
                                        {emailStatus.emailServiceConfigured ? 'Email Service Configured' : 'Email Service Not Configured'}
                                    </AlertTitle>
                                    <AlertDescription>{emailStatus.message}</AlertDescription>
                                </Alert>

                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="flex items-center gap-2">
                                        {emailStatus.resendApiKeyPresent ? (
                                            <CheckCircle className="h-4 w-4 text-green-600" />
                                        ) : (
                                            <XCircle className="h-4 w-4 text-red-600" />
                                        )}
                                        <span>Resend API Key</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {emailStatus.adminEmailConfigured ? (
                                            <CheckCircle className="h-4 w-4 text-green-600" />
                                        ) : (
                                            <XCircle className="h-4 w-4 text-orange-600" />
                                        )}
                                        <span>Admin Email</span>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <hr className="border-border" />

                    {/* Send Test Email Form */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Send Test Email</h3>

                        <div className="space-y-2">
                            <Label htmlFor="recipient">Recipient Email *</Label>
                            <Input
                                id="recipient"
                                type="email"
                                placeholder="test@example.com"
                                value={recipientEmail}
                                onChange={(e) => setRecipientEmail(e.target.value)}
                                disabled={sendTestEmail.isLoading}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="template">Email Template *</Label>
                            <Select
                                value={templateType}
                                onValueChange={(value: any) => setTemplateType(value)}
                                disabled={sendTestEmail.isLoading}
                            >
                                <SelectTrigger id="template">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="welcome">🎉 Welcome Email</SelectItem>
                                    <SelectItem value="verification">✅ Verification Email</SelectItem>
                                    <SelectItem value="approval">🎊 Approval Email</SelectItem>
                                    <SelectItem value="notification">🔔 Notification Email</SelectItem>
                                    <SelectItem value="custom">✏️ Custom Email</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {templateType === 'custom' && (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="subject">Custom Subject</Label>
                                    <Input
                                        id="subject"
                                        placeholder="Enter email subject"
                                        value={customSubject}
                                        onChange={(e) => setCustomSubject(e.target.value)}
                                        disabled={sendTestEmail.isLoading}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="message">Custom Message</Label>
                                    <Textarea
                                        id="message"
                                        placeholder="Enter your custom message"
                                        value={customMessage}
                                        onChange={(e) => setCustomMessage(e.target.value)}
                                        rows={5}
                                        disabled={sendTestEmail.isLoading}
                                    />
                                </div>
                            </>
                        )}

                        <Button
                            onClick={handleSendTest}
                            disabled={sendTestEmail.isLoading || !emailStatus?.emailServiceConfigured}
                            className="w-full"
                            size="lg"
                        >
                            {sendTestEmail.isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <Send className="mr-2 h-4 w-4" />
                                    Send Test Email
                                </>
                            )}
                        </Button>
                    </div>

                    {/* Instructions */}
                    <Alert>
                        <Mail className="h-4 w-4" />
                        <AlertTitle>Testing Instructions</AlertTitle>
                        <AlertDescription className="text-sm space-y-1">
                            <p>1. Enter a valid email address (your own for testing)</p>
                            <p>2. Select an email template or create a custom one</p>
                            <p>3. Click Send Test Email and check your inbox</p>
                            <p className="text-muted-foreground mt-2">
                                <strong>Note:</strong> If using Resend test domain, emails will only be sent to your verified email address.
                            </p>
                        </AlertDescription>
                    </Alert>
                </CardContent>
            </Card>
        </div>
    );
};

export default EmailTestPage;
